import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO configuration per route
 * - canonical: Full URL for rel="canonical"
 * - noindex: If true, sets robots to "noindex, nofollow"
 * - title: Page title
 * - description: Meta description
 */
const seoConfig = {
  '/': {
    canonical: 'https://skogskalkyl.se/',
    title: 'Skogskalkyl – oberoende skogskalkylatorer',
    description: 'Oberoende skogskalkylatorer för svenska skogsägare. Räkna ut volym, värde, avverkning och fastighetsvärde med data från Skogsstyrelsen och SLU.'
  },
  '/verktyg': {
    canonical: 'https://skogskalkyl.se/verktyg',
    title: 'Skogskalkyl Verktyg | Fastighetsvärde, avverkning och gallring',
    description: 'Välj rätt kalkylator för din skog. Räkna ut fastighetsvärde, avverkning och gallring direkt i webbläsaren.'
  },
  '/faq': {
    canonical: 'https://skogskalkyl.se/faq',
    title: 'Vanliga frågor | Skogskalkyl',
    description: 'Svar på vanliga frågor om Skogskalkyl och de oberoende kalkylatorerna.'
  },
  '/metodik': {
    canonical: 'https://skogskalkyl.se/metodik',
    title: 'Metodik och datakällor | Skogskalkyl',
    description: 'Läs om datakällor och beräkningsmetoder bakom Skogskalkyl. Baserat på SLU Riksskogstaxeringen och Skogsstyrelsens statistik.'
  },
  '/about': {
    canonical: 'https://skogskalkyl.se/about',
    title: 'Om Skogskalkyl',
    description: 'Om Skogskalkyl – oberoende skogskalkylatorer från Hodema edertools AB.'
  }
};

// Default values
const defaultSeo = {
  title: 'Skogskalkyl – oberoende skogskalkylatorer',
  description: 'Oberoende skogskalkylatorer för svenska skogsägare. Räkna ut volym, värde, avverkning och fastighetsvärde med data från Skogsstyrelsen och SLU.',
  canonical: 'https://skogskalkyl.se/'
};

/**
 * Hook to manage SEO meta tags per route
 * Updates canonical, robots, title, and description based on current path
 */
export const useSeoMeta = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Get base path without query params
    const path = location.pathname;
    const config = seoConfig[path] || {};
    
    // Update title
    document.title = config.title || defaultSeo.title;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && config.description) {
      metaDesc.setAttribute('content', config.description);
    } else if (metaDesc && !config.description) {
      metaDesc.setAttribute('content', defaultSeo.description);
    }
    
    // Update canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink && config.canonical) {
      canonicalLink.setAttribute('href', config.canonical);
    } else if (canonicalLink && !config.canonical) {
      canonicalLink.setAttribute('href', defaultSeo.canonical);
    }
    
    // Update robots meta
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) {
      if (config.noindex) {
        robotsMeta.setAttribute('content', 'noindex, nofollow');
      } else {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }
    
  }, [location.pathname]);
};

export default useSeoMeta;
